import EventLayout from '@/components/layouts/EventLayout'
import { PageHeader } from '@/components/patterns'
import { Switch, message } from '@/components/ui'
import { api } from '@/lib/apiClient'
import { Head } from '@inertiajs/react'
import { useState } from 'react'
import { HiOutlineCog } from 'react-icons/hi'

export type PhotoGalleryPageProps = {
  eventId: number
  eventName: string
  eventSlug: string
  photoGallerySetting: {
    enabled: boolean
    share_to_social_enabled: boolean
  }
}

export default function PhotoGalleryPage({
  eventName,
  eventSlug,
  photoGallerySetting,
}: PhotoGalleryPageProps) {
  const [isPhotoGalleryEnabled, setIsPhotoGalleryEnabled] = useState<boolean>(
    photoGallerySetting?.enabled ?? false
  )
  const [isShareToSocialEnabled, setIsShareToSocialEnabled] = useState<boolean>(
    photoGallerySetting?.share_to_social_enabled ?? false
  )
  const [isLoading, setIsLoading] = useState(false)

  const handlePhotoGalleryToggle = async (checked: boolean) => {
    const nextShareToSocialEnabled = checked ? isShareToSocialEnabled : false
    setIsPhotoGalleryEnabled(checked)
    setIsShareToSocialEnabled(nextShareToSocialEnabled)
    await updateSettings({
      enabled: checked,
      share_to_social_enabled: nextShareToSocialEnabled,
    })
  }

  const handleShareToSocialToggle = async (checked: boolean) => {
    setIsShareToSocialEnabled(checked)
    await updateSettings({
      enabled: isPhotoGalleryEnabled,
      share_to_social_enabled: checked,
    })
  }

  const updateSettings = async (settings: {
    enabled: boolean
    share_to_social_enabled: boolean
  }) => {
    setIsLoading(true)
    try {
      const { data } = await api.patch<any>(`/events/${eventSlug}/photo_gallery`, {
        photo_gallery_setting: {
          enabled: settings.enabled,
          share_to_social_enabled: settings.share_to_social_enabled,
        },
      })
      const nextSetting = data?.photoGallerySetting
      if (nextSetting) {
        setIsPhotoGalleryEnabled(!!nextSetting.enabled)
        setIsShareToSocialEnabled(!!nextSetting.share_to_social_enabled)
      }
      message.success('Photo Gallery settings updated successfully.')
    } catch (error) {
      console.error('Error updating photo gallery settings:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <EventLayout>
      <Head title={`${eventName} - Photo Gallery`} />
      <PageHeader
        title="Photo Gallery"
        subtitle="Configure the Photo Gallery feature for your event."
      />
      <div className="p-4">
        <div className="bg-white flex flex-col max-w-[50%] border border-gray-100">
          <div className="px-8 py-5 border-b border-gray-200">
            <p className="flex items-center gap-2 m-0 text-xl leading-5">
              <HiOutlineCog /> Settings
            </p>
          </div>
          <div className="p-8">
            <div className="flex flex-row justify-between items-center">
              <div>
                <p className="text-sm font-medium leading-6">Photo Gallery</p>
                <p className="text-sm text-black/40 font-normal">
                  Enable to display the Photo Gallery feature.
                </p>
              </div>
              <Switch
                checked={isPhotoGalleryEnabled}
                onChange={handlePhotoGalleryToggle}
                disabled={isLoading}
              />
            </div>
            <div className="flex flex-row justify-between items-center pt-6">
              <div>
                <p className="text-sm font-medium leading-6">Social Sharing</p>
                <p className="text-sm text-black/40 font-normal">
                  Allow attendees to share their photos to social media.
                </p>
              </div>
              <Switch
                checked={isShareToSocialEnabled}
                onChange={handleShareToSocialToggle}
                disabled={isLoading || !isPhotoGalleryEnabled}
              />
            </div>
          </div>
        </div>
      </div>
    </EventLayout>
  )
}
